import { useState, useEffect } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useForm, Controller } from "react-hook-form";
import Input from "../../ChildComponents/Input";
import BrandButton from "../../ChildComponents/BrandButton";
import Select from "../../ChildComponents/Select";
import SmartImageUpload from "../../ChildComponents/SmartImageUploader";
import useAuthToken from "../../Hooks/useAuthToken";
import { repoterschema } from "../validation/reportersValidation";
import { useGetlocation } from "../../store/api/locationApi";
import { useRegister, useUpdateUser } from "../../store/api/authApi";
import { useGetRole } from "../../store/api/roleApi";
import { apiRequest } from "../../store/api/api";

function RoleForm({ data, editId, onSuccessCreated }) {
  const queryClient = useQueryClient();
  const token = useAuthToken();
  const updateUser = useUpdateUser(editId?._id || null);
  const createRegister = useRegister();
  const getRole = useGetRole();

  const {
    data: roleData,
    isLoading: isRoleLoading,
    error: roleError,
  } = useQuery({ queryKey: "role", queryFn: getRole });

  const {
    data: allDepartment,
    isLoading: isSectionLoading,
    error: sectionError,
  } = useQuery({
    queryKey: ["department"],
    queryFn: () => apiRequest("get", "/api/team/department", null, token),
  });

  const transformedRole = roleData?.role?.map((role) => ({
    label: role.name,
    value: role._id,
  }));

  const transformedDepartment = allDepartment?.allDepartment?.map((role) => ({
    label: role.title,
    value: role._id,
  }));

  const getLocation = useGetlocation();
  const {
    data: locationData,
    isLoading: isLocationLoading,
    error: locationError,
  } = useQuery({ queryKey: "location", queryFn: getLocation });

  const transformedDivistion = locationData?.division?.map((i) => ({
    label: i.name,
    value: i._id,
  }));
  const transformedDistrict = locationData?.district?.map((i) => ({
    label: i.name,
    value: i._id,
  }));
  const transformedSubDistrict = locationData?.upazila?.map((i) => ({
    label: i.name,
    value: i._id,
  }));

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
    watch,
  } = useForm({
    resolver: yupResolver(repoterschema),
  });

  useEffect(() => {
    if (editId && locationData) {
      const selectedDivision = transformedDivistion?.find(
        (item) => item.value === editId.division
      );
      const selectedDistrict = transformedDistrict?.find(
        (item) => item.value === editId.district
      );
      const selectedSubDistrict = transformedSubDistrict?.find(
        (item) => item.value === editId.subDistrict
      );
      const selectedRole = transformedRole?.find(
        (item) => item.value === editId.role?._id
      );

      const selectedDepartment = transformedDepartment?.find(
        (item) => item.value === editId.department
      );

      reset({
        fullName: editId.fullName ?? "",
        email: editId.email ?? "",
        division: selectedDivision || null,
        district: selectedDistrict || null,
        subDistrict: selectedSubDistrict || null,
        role: selectedRole || null,
        department: selectedDepartment || null,
        fontStatus: editId.fontStatus,
        userStatus: editId.userStatus,
        department: selectedDepartment || null,
        image: editId.image,
        serial: editId.serial || 1,
      });

      setEdited(true);
    }
  }, [editId, locationData, reset]);

  const [loading, setLoading] = useState(false);
  const [edited, setEdited] = useState(editId ? true : false);

  const mutation = useMutation({
    mutationFn: (data) => {
      return editId ? updateUser(data) : createRegister(data);
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries(["repoter"]);
      setLoading(false);
      toast.success("Updated Successfully");
      reset();
      onSuccessCreated(1);
    },
    onError: (error) => {
      toast.error(error.message);
      setLoading(false);
    },
  });

  const handleFormSubmit = (e) => {
    e.preventDefault();
    handleSubmit(onSubmit)(e);
  };

  const onSubmit = (formData) => {
    // setLoading(true);
    try {
      const data = new FormData();
      data.append("fullName", formData.fullName);
      data.append("role", formData.role?.value);
      data.append("division", formData.division?.value);
      data.append("district", formData.district?.value);
      data.append("subDistrict", formData.subDistrict?.value);
      data.append("department", formData.department?.value);
      data.append("fontStatus", formData.fontStatus);
      data.append("email", formData.email);
      data.append("userStatus", formData.userStatus);
      data.append("serial", formData.serial);

      // data.append("password", formData.password);
      if (formData.password) {
        data.append("password", formData.password);
      }
      if (formData.image) {
        data.append("image", formData.image);
      }

      // ✅ FormData console log properly
      for (let pair of data.entries()) {
        console.log(pair[0] + ":", pair[1]);
      }

      mutation.mutate(data);
    } catch (error) {
      setLoading(false);
      console.error("Caught error in handleSubmit:", error);
      toast.error("Form submission failed: " + error.message);
    }
  };

  return (
    <div className="container-fluid py-4">
      <div className="card shadow-lg border-0 rounded-4">
        <div
          className="card-header bg-gradient border-0 py-4"
          style={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          }}
        >
          <div className="d-flex align-items-center">
            <div className="bg-white bg-opacity-25 rounded-3 p-2 me-3">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="24"
                height="24"
                fill="white"
                viewBox="0 0 16 16"
              >
                <path d="M1 14s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1H1zm5-6a3 3 0 1 0 0-6 3 3 0 0 0 0 6z" />
                <path
                  fillRule="evenodd"
                  d="M13.5 5a.5.5 0 0 1 .5.5V7h1.5a.5.5 0 0 1 0 1H14v1.5a.5.5 0 0 1-1 0V8h-1.5a.5.5 0 0 1 0-1H13V5.5a.5.5 0 0 1 .5-.5z"
                />
              </svg>
            </div>
            <div>
              <h4 className="mb-0 text-white fw-bold">
                {editId ? "Edit User Profile" : "Create New User"}
              </h4>
              <p className="mb-0 text-white-50 small">
                {editId
                  ? "Update user information and settings"
                  : "Fill in the details to create a new user account"}
              </p>
            </div>
          </div>
        </div>

        <div className="card-body p-4 p-lg-5">
          <div onSubmit={handleFormSubmit}>
            <div className="row g-4">
              <div className="col-lg-9">
                {/* Personal Information Section */}
                <div className="mb-5">
                  <div className="d-flex align-items-center mb-4">
                    <div className="bg-primary bg-opacity-10 rounded-circle p-2 me-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        fill="currentColor"
                        className="text-primary"
                        viewBox="0 0 16 16"
                      >
                        <path d="M11 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0z" />
                        <path
                          fillRule="evenodd"
                          d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8zm8-7a7 7 0 0 0-5.468 11.37C3.242 11.226 4.805 10 8 10s4.757 1.225 5.468 2.37A7 7 0 0 0 8 1z"
                        />
                      </svg>
                    </div>
                    <div>
                      <h5 className="mb-0 fw-semibold text-dark">
                        Personal Information
                      </h5>
                      <small className="text-muted">
                        Basic user details and contact information
                      </small>
                    </div>
                  </div>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <Input
                        icon="title"
                        {...register("fullName")}
                        error={errors.name?.message}
                        type="text"
                        placeholder="Mr. Rohim"
                        label="Full Name"
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        icon="title"
                        {...register("email")}
                        error={errors.name?.message}
                        type="email"
                        placeholder="admin@admin.com"
                        label="Email Address"
                      />
                    </div>
                  </div>
                </div>

                {/* Role & Department Section */}
                <div className="mb-5">
                  <div className="d-flex align-items-center mb-4">
                    <div className="bg-success bg-opacity-10 rounded-circle p-2 me-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        fill="currentColor"
                        className="text-success"
                        viewBox="0 0 16 16"
                      >
                        <path d="M6.5 1A1.5 1.5 0 0 0 5 2.5V3H1.5A1.5 1.5 0 0 0 0 4.5v8A1.5 1.5 0 0 0 1.5 14h13a1.5 1.5 0 0 0 1.5-1.5v-8A1.5 1.5 0 0 0 14.5 3H11v-.5A1.5 1.5 0 0 0 9.5 1h-3zm0 1h3a.5.5 0 0 1 .5.5V3H6v-.5a.5.5 0 0 1 .5-.5zm1.886 6.914L15 7.151V12.5a.5.5 0 0 1-.5.5h-13a.5.5 0 0 1-.5-.5V7.15l6.614 1.764a1.5 1.5 0 0 0 .772 0zM1.5 4h13a.5.5 0 0 1 .5.5v1.616L8.129 7.948a.5.5 0 0 1-.258 0L1 6.116V4.5a.5.5 0 0 1 .5-.5z" />
                      </svg>
                    </div>
                    <div>
                      <h5 className="mb-0 fw-semibold text-dark">
                        Role & Department
                      </h5>
                      <small className="text-muted">
                        Assign user role and department
                      </small>
                    </div>
                  </div>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <Controller
                        name="role"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            isClearable
                            options={transformedRole}
                            placeholder="Select a Role"
                            label="User Role"
                          />
                        )}
                      />
                    </div>
                    <div className="col-md-6">
                      <Controller
                        name="department"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            isClearable
                            options={transformedDepartment}
                            placeholder="Select a Department"
                            label="Department"
                          />
                        )}
                      />
                    </div>
                    <div className="col-md-6">
                      <Input
                        icon="title"
                        {...register("serial")}
                        error={errors.name?.message}
                        type="number"
                        placeholder="1 or 2 or 3"
                        label="Serial Number [Only Input Number]"
                      />
                    </div>
                  </div>
                </div>

                {/* Location Information Section */}
                <div className="mb-5">
                  <div className="d-flex align-items-center mb-4">
                    <div className="bg-info bg-opacity-10 rounded-circle p-2 me-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        fill="currentColor"
                        className="text-info"
                        viewBox="0 0 16 16"
                      >
                        <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10zm0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6z" />
                      </svg>
                    </div>
                    <div>
                      <h5 className="mb-0 fw-semibold text-dark">
                        Location Details
                      </h5>
                      <small className="text-muted">
                        Select division, district and upazila
                      </small>
                    </div>
                  </div>

                  <div className="row g-3">
                    <div className="col-md-4">
                      <Controller
                        name="division"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            isClearable
                            options={transformedDivistion}
                            placeholder="Select Division"
                            label="Division"
                          />
                        )}
                      />
                    </div>
                    <div className="col-md-4">
                      <Controller
                        name="district"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            isClearable
                            options={transformedDistrict}
                            placeholder="Select District"
                            label="District"
                          />
                        )}
                      />
                    </div>
                    <div className="col-md-4">
                      <Controller
                        name="subDistrict"
                        control={control}
                        render={({ field }) => (
                          <Select
                            {...field}
                            isClearable
                            options={transformedSubDistrict}
                            placeholder="Select Upazila"
                            label="Upazila"
                          />
                        )}
                      />
                    </div>
                  </div>
                </div>

                {/* Security Section */}
                <div className="mb-4">
                  <div className="d-flex align-items-center mb-4">
                    <div className="bg-warning bg-opacity-10 rounded-circle p-2 me-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        fill="currentColor"
                        className="text-warning"
                        viewBox="0 0 16 16"
                      >
                        <path d="M5.338 1.59a61.44 61.44 0 0 0-2.837.856.481.481 0 0 0-.328.39c-.554 4.157.726 7.19 2.253 9.188a10.725 10.725 0 0 0 2.287 2.233c.346.244.652.42.893.533.12.057.218.095.293.118a.55.55 0 0 0 .101.025.615.615 0 0 0 .1-.025c.076-.023.174-.061.294-.118.24-.113.547-.29.893-.533a10.726 10.726 0 0 0 2.287-2.233c1.527-1.997 2.807-5.031 2.253-9.188a.48.48 0 0 0-.328-.39c-.651-.213-1.75-.56-2.837-.855C9.552 1.29 8.531 1.067 8 1.067c-.53 0-1.552.223-2.662.524zM5.072.56C6.157.265 7.31 0 8 0s1.843.265 2.928.56c1.11.3 2.229.655 2.887.87a1.54 1.54 0 0 1 1.044 1.262c.596 4.477-.787 7.795-2.465 9.99a11.775 11.775 0 0 1-2.517 2.453 7.159 7.159 0 0 1-1.048.625c-.28.132-.581.24-.829.24s-.548-.108-.829-.24a7.158 7.158 0 0 1-1.048-.625 11.777 11.777 0 0 1-2.517-2.453C1.928 10.487.545 7.169 1.141 2.692A1.54 1.54 0 0 1 2.185 1.43 62.456 62.456 0 0 1 5.072.56z" />
                        <path d="M9.5 6.5a1.5 1.5 0 0 1-1 1.415l.385 1.99a.5.5 0 0 1-.491.595h-.788a.5.5 0 0 1-.49-.595l.384-1.99a1.5 1.5 0 1 1 2-1.415z" />
                      </svg>
                    </div>
                    <div>
                      <h5 className="mb-0 fw-semibold text-dark">Security</h5>
                      <small className="text-muted">
                        {editId
                          ? "Leave blank to keep current password"
                          : "Set a secure password"}
                      </small>
                    </div>
                  </div>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <Input
                        icon="title"
                        {...register("password")}
                        error={errors.name?.message}
                        type="password"
                        placeholder="*********"
                        label={editId ? "New Password (Optional)" : "Password"}
                      />
                    </div>
                  </div>
                </div>

                {/* Font Status Section */}
                <div className="mb-4">
                  <div className="d-flex align-items-center mb-4">
                    <div className="bg-danger bg-opacity-10 rounded-circle p-2 me-3">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="20"
                        height="20"
                        fill="currentColor"
                        className="text-danger"
                        viewBox="0 0 16 16"
                      >
                        <path d="M5.52.359A.5.5 0 0 1 6 0h4a.5.5 0 0 1 .474.658L8.694 6H12.5a.5.5 0 0 1 .395.807l-7 9a.5.5 0 0 1-.873-.454L6.823 9.5H3.5a.5.5 0 0 1-.48-.641l2.5-8.5z" />
                      </svg>
                    </div>
                    <div>
                      <h5 className="mb-0 fw-semibold text-dark">
                        Font Status
                      </h5>
                      <small className="text-muted">
                        Enable or disable font access
                      </small>
                    </div>
                  </div>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <Controller
                        name="fontStatus"
                        control={control}
                        defaultValue={false}
                        render={({ field }) => (
                          <div className="form-check form-switch p-3 bg-light rounded-3">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id="fontStatusSwitch"
                              checked={field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                              style={{
                                width: "3em",
                                height: "1.5em",
                                cursor: "pointer",
                              }}
                            />
                            <label
                              className="form-check-label ms-2 fw-semibold"
                              htmlFor="fontStatusSwitch"
                            >
                              {field.value ? (
                                <span className="text-success">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    fill="currentColor"
                                    className="me-2"
                                    viewBox="0 0 16 16"
                                  >
                                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
                                  </svg>
                                  Font Enabled
                                </span>
                              ) : (
                                <span className="text-muted">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    fill="currentColor"
                                    className="me-2"
                                    viewBox="0 0 16 16"
                                  >
                                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z" />
                                  </svg>
                                  Font Disabled
                                </span>
                              )}
                            </label>
                          </div>
                        )}
                      />
                    </div>
                    <div className="col-md-6">
                      <Controller
                        name="userStatus"
                        control={control}
                        defaultValue={false}
                        render={({ field }) => (
                          <div className="form-check form-switch p-3 bg-light rounded-3">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              role="switch"
                              id="userStatusSwitch"
                              checked={field.value}
                              onChange={(e) => field.onChange(e.target.checked)}
                              style={{
                                width: "3em",
                                height: "1.5em",
                                cursor: "pointer",
                              }}
                            />
                            <label
                              className="form-check-label ms-2 fw-semibold"
                              htmlFor="userStatusSwitch"
                            >
                              {field.value ? (
                                <span className="text-success">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    fill="currentColor"
                                    className="me-2"
                                    viewBox="0 0 16 16"
                                  >
                                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z" />
                                  </svg>
                                  User Status Enabled
                                </span>
                              ) : (
                                <span className="text-muted">
                                  <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    width="16"
                                    height="16"
                                    fill="currentColor"
                                    className="me-2"
                                    viewBox="0 0 16 16"
                                  >
                                    <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zM5.354 4.646a.5.5 0 1 0-.708.708L7.293 8l-2.647 2.646a.5.5 0 0 0 .708.708L8 8.707l2.646 2.647a.5.5 0 0 0 .708-.708L8.707 8l2.647-2.646a.5.5 0 0 0-.708-.708L8 7.293 5.354 4.646z" />
                                  </svg>
                                  User Status Disabled
                                </span>
                              )}
                            </label>
                          </div>
                        )}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Image Upload Section */}
              <div className="col-lg-3">
                <div className="position-sticky" style={{ top: "20px" }}>
                  <div className="upload-section p-4 rounded-3">
                    <div className="text-center mb-4">
                      <div className="d-flex align-items-center justify-content-center mb-3">
                        <div className="bg-secondary bg-opacity-10 rounded-circle p-2 me-2">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="20"
                            height="20"
                            fill="currentColor"
                            className="text-secondary"
                            viewBox="0 0 16 16"
                          >
                            <path d="M6.002 5.5a1.5 1.5 0 1 1-3 0 1.5 1.5 0 0 1 3 0z" />
                            <path d="M2.002 1a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V3a2 2 0 0 0-2-2h-12zm12 1a1 1 0 0 1 1 1v6.5l-3.777-1.947a.5.5 0 0 0-.577.093l-3.71 3.71-2.66-1.772a.5.5 0 0 0-.63.062L1.002 12V3a1 1 0 0 1 1-1h12z" />
                          </svg>
                        </div>
                        <h5 className="mb-0 fw-semibold text-dark">
                          Profile Photo
                        </h5>
                      </div>
                      <small className="text-muted d-block mb-3">
                        Upload a profile picture
                      </small>
                    </div>

                    <Controller
                      name="image"
                      control={control}
                      defaultValue={null}
                      render={({ field }) => (
                        <SmartImageUpload
                          multiple={false}
                          limit={1}
                          files={field.value ? [field.value] : []}
                          onSelectFiles={(url) => {
                            console.log(url);
                            field.onChange(url.data[0].image || "");
                          }}
                        />
                      )}
                    />

                    <div
                      className="alert alert-light border mt-3 mb-0"
                      role="alert"
                    >
                      <small className="text-muted">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          fill="currentColor"
                          className="me-2"
                          viewBox="0 0 16 16"
                        >
                          <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                          <path d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252 1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0-.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0 1 1 0 0 1 2 0z" />
                        </svg>
                        Recommended: Square image, min 400x400px
                      </small>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="row mt-5">
              <div className="col-12">
                <div className="d-flex gap-3 justify-content-end border-top pt-4">
                  <button
                    type="button"
                    className="btn btn-light btn-lg px-4 rounded-pill"
                    onClick={() => reset()}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="currentColor"
                      className="me-2"
                      viewBox="0 0 16 16"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8 15A7 7 0 1 0 8 1a7 7 0 0 0 0 14zm0 1A8 8 0 1 1 8 0a8 8 0 0 1 0 16z"
                      />
                      <path
                        fillRule="evenodd"
                        d="M11.854 4.146a.5.5 0 0 1 0 .708l-7 7a.5.5 0 0 1-.708-.708l7-7a.5.5 0 0 1 .708 0z"
                      />
                      <path
                        fillRule="evenodd"
                        d="M4.146 4.146a.5.5 0 0 0 0 .708l7 7a.5.5 0 0 0 .708-.708l-7-7a.5.5 0 0 0-.708 0z"
                      />
                    </svg>
                    Reset
                  </button>
                  <BrandButton
                    type="button"
                    onClick={handleFormSubmit}
                    title={
                      loading
                        ? "Saving..."
                        : editId
                        ? "Update User"
                        : "Create User"
                    }
                    className="btn-lg px-5 rounded-pill"
                    disabled={loading}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        .card {
          transition: all 0.3s ease;
        }
        
        .card-header {
          position: relative;
          overflow: hidden;
        }
        
        .card-header::before {
          content: '';
          position: absolute;
          top: -50%;
          right: -50%;
          width: 200%;
          height: 200%;
          background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
          animation: pulse 15s ease-in-out infinite;
        }
        
        @keyframes pulse {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(-10%, -10%) scale(1.1); }
        }
        
        .form-control:focus,
        .form-select:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.15);
        }
        
        .btn-lg {
          font-weight: 500;
          transition: all 0.3s ease;
        }
        
        .btn-lg:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
        
        .upload-section {
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
        }
      `}</style>
    </div>
  );
}

export default RoleForm;
